<template>
  <IdentityShell>
    <div class="directory-verify">
      <p class="kicker">
        {{ $t('auth.identity.directoryVerify.verify', 'Verify') }}
      </p>

      <h1 class="section-headline directory-verify__title">
        {{ $t('auth.identity.directoryVerify.checkYour', 'Check your') }}
        <span class="highlight">{{
          $t('auth.identity.directoryVerify.emailHighlight', 'email')
        }}</span
        >{{ $t('auth.identity.directoryVerify.emailTrail', '.') }}
      </h1>
      <p class="section-lede directory-verify__lede">
        {{
          $t(
            'auth.identity.directoryVerify.sentCodeIntro',
            'We sent a 6-digit code to'
          )
        }}
        <strong>{{ displayEmail }}</strong
        >{{
          $t(
            'auth.identity.directoryVerify.sentCodeOutro',
            '. It expires in 15 minutes.'
          )
        }}
      </p>

      <AlertDanger v-if="firstError" show class="mb-3">
        {{ firstError }}
      </AlertDanger>

      <FormBase class="auth-form" @submit.prevent="onSubmit">
        <FormGroupBase
          class="mb-3"
          :label="$t('auth.identity.directoryVerify.codeLabel', '6-digit code')"
          :state="fieldState('code')"
          :invalid-feedback="fieldError('code')"
        >
          <InputText
            v-model="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            autofocus
            maxlength="6"
            :placeholder="
              $t('auth.identity.directoryVerify.codePlaceholder', '123456')
            "
            :state="fieldState('code')"
            :ignore1-password="true"
            class="directory-verify__code"
          />
        </FormGroupBase>

        <button
          type="submit"
          class="btn-communal btn-communal--block"
          :disabled="submitting"
        >
          <span v-if="!submitting">
            {{ $t('auth.identity.directoryVerify.continue', 'Continue') }}
            <span class="btn-communal__arrow" aria-hidden="true">→</span>
          </span>
          <span v-else>
            {{ $t('auth.identity.directoryVerify.verifying', 'Verifying…') }}
          </span>
        </button>
      </FormBase>

      <p class="auth-foot-link">
        {{ $t('auth.identity.directoryVerify.didntGetIt', "Didn't get it?") }}
        <RouterLink to="/login">
          {{
            $t(
              'auth.identity.directoryVerify.sendAnotherCode',
              'Send another code'
            )
          }}
        </RouterLink>
      </p>
    </div>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import { IdentityShell } from '@/components/feature/Identity';
import { AlertDanger } from '@/components/ui/Alert';
import { InputText } from '@/components/ui/Form';
import { useIdentityBoot } from '@/composables/useIdentityBoot';
import { useIdentityForm } from '@/composables/useIdentityForm';

const route = useRoute();
const boot = useIdentityBoot();
const { t } = useI18n({ useScope: 'global' });

const bootEmail =
  ((boot.data as { email?: string } | undefined)?.email ?? '') || '';
const queryEmail =
  typeof route.query.email === 'string' ? route.query.email : '';
const initialEmail = bootEmail || queryEmail;

const email = ref(initialEmail);
const code = ref('');

const displayEmail = computed(
  () =>
    email.value ||
    t('auth.identity.directoryVerify.thatAddressFallback', 'that address')
);

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/login/verify' });

async function onSubmit() {
  await submit({ email: email.value, code: code.value });
}
</script>

<style lang="scss" scoped>
// Rendered inside IdentityShell's .communal-surface scope — raw <h1>, raw
// submit <button>, .section-headline, and .btn-communal are deliberate
// marketing-surface classes, not in-product wrappers. See IdentityShell.vue
// for the full contract.

.directory-verify__title {
  margin-bottom: 0.625rem;
}

.directory-verify__lede {
  margin-bottom: 1.5rem;

  strong {
    color: var(--ink-900);
    font-weight: 600;
  }
}

.directory-verify__code {
  font-feature-settings:
    'tnum' 1,
    'lnum' 1;
  font-size: 1.5rem;
  letter-spacing: 0.32em;
  text-align: center;
  font-weight: 600;
}
</style>
